import React, { useEffect } from "react";
import "../../styles/LegalPages.css";

export default function RefundPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page-shell">
      <div className="legal-page-container">
        <header className="legal-hero-card">
          <div className="legal-top-badge-row">
            <span className="legal-badge-pill">Policy</span>
            <span className="legal-last-updated">Effective: July 1st, 2026</span>
          </div>
          <h1 className="legal-hero-title">Refund & Cancellation Policy</h1>
          <p className="legal-hero-subtitle">
            At Creators Dost, we provide digital educational products, online courses, tutorials, training programs, and digital learning resources. Due to the digital nature of our products and the fact that course access may be provided immediately after successful payment, Creators Dost does not provide refunds or returns for purchased courses or digital products.
          </p>
        </header>

        <nav className="legal-switcher-bar">
          <a href="#privacy-policy" className="legal-switch-btn">Privacy Policy</a>
          <a href="#terms-and-conditions" className="legal-switch-btn">Terms & Conditions</a>
          <a href="#refund-policy" className="legal-switch-btn active">Return & Refund</a>
        </nav>

        <div className="legal-clauses-stack">
          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">01</span>
              <h3>No Refund Policy</h3>
            </div>
            <p><strong>REFUND FACILITY IS NOT AVAILABLE.</strong></p>
            <p style={{ marginTop: "8px" }}>
              All purchases made through Creators Dost are final and non-refundable once the payment has been successfully completed and access to the purchased course or digital product has been provided.
            </p>
            <p>
              Once course content becomes available to the user, the purchase cannot be returned or cancelled for a refund.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">02</span>
              <h3>Before Making a Purchase</h3>
            </div>
            <p>Users are strongly advised to carefully review:</p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Course description and detailed syllabus</li>
              <li><span className="dot">✓</span> Course duration and curriculum structure</li>
              <li><span className="dot">✓</span> Key features and downloadable resources</li>
              <li><span className="dot">✓</span> Mentor and instructor background information</li>
              <li><span className="dot">✓</span> Technical hardware/software requirements</li>
              <li><span className="dot">✓</span> Available previews or video demonstrations</li>
              <li><span className="dot">✓</span> Access conditions, duration, and device policies</li>
              <li><span className="dot">✓</span> Applicable promotional offers, taxes, and final pricing</li>
            </ul>
            <p style={{ marginTop: "12px" }}>
              By making a purchase, the user confirms that they have reviewed and accepted the available course information and this Refund Policy.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">03</span>
              <h3>No Refund for Change of Mind</h3>
            </div>
            <p>Refunds will not be provided under any of the following circumstances:</p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Change of mind or personal preference</li>
              <li><span className="dot">✓</span> Change in personal or professional circumstances</li>
              <li><span className="dot">✓</span> Lack of time to complete or follow the lessons</li>
              <li><span className="dot">✓</span> Failure to attend, watch, or stream classes</li>
              <li><span className="dot">✓</span> Failure to utilize course access</li>
              <li><span className="dot">✓</span> Lack of interest after completing the checkout</li>
              <li><span className="dot">✓</span> Failure to achieve subjective expected results</li>
              <li><span className="dot">✓</span> Change in career trajectory or learning objectives</li>
              <li><span className="dot">✓</span> Not liking the course presentation or instructor after purchase</li>
              <li><span className="dot">✓</span> Failure to finish the full modules or assignments</li>
              <li><span className="dot">✓</span> Purchasing the wrong course due to personal user error</li>
            </ul>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">04</span>
              <h3>No Refund for Course Outcomes</h3>
            </div>
            <p>
              Creators Dost provides educational and skill-development content. We do not guarantee specific outcomes, including:
            </p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Employment or corporate placements</li>
              <li><span className="dot">✓</span> Monetary income or earnings targets</li>
              <li><span className="dot">✓</span> Freelance contracts, gigs, or clients</li>
              <li><span className="dot">✓</span> YouTube or social media subscriber growth</li>
              <li><span className="dot">✓</span> Viewership spikes or engagement metrics</li>
              <li><span className="dot">✓</span> Commercial business expansion</li>
              <li><span className="dot">✓</span> Any particular professional outcome</li>
            </ul>
            <p style={{ marginTop: "12px" }}>
              Therefore, failure to achieve a particular result does not qualify for a refund.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">05</span>
              <h3>Technical Issues</h3>
            </div>
            <p>
              If you experience a genuine technical issue that prevents you from accessing a purchased course, please reach out directly to our support desk. We will investigate and, where technically feasible, assist in resolving the platform access block.
            </p>
            <p>
              Technical troubleshooting or restoring curriculum access does not constitute grounds for a refund.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">06</span>
              <h3>Duplicate or Failed Transactions</h3>
            </div>
            <p>
              If a payment appears to have failed at checkout but the amount was deducted from your bank or card account, please notify our team with the full transaction reference details. Creators Dost will verify the transaction status with the payment gateway and take the appropriate corrective steps.
            </p>
            <p>
              Resolution of duplicate or failed payment gateway attempts does not imply that refunds are available for delivered courses.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">07</span>
              <h3>Order Cancellation</h3>
            </div>
            <p>
              Once a course enrollment has been completed and digital access has been granted, the order cannot be revoked, paused, or cancelled by the user to claim a refund.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">08</span>
              <h3>Promotional Offers and Discounts</h3>
            </div>
            <p>
              Discounts, seasonal promotional campaigns, coupons, or special pricing tiers offered by Creators Dost do not alter or nullify this Refund Policy unless expressly stated in the terms of that specific campaign.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">09</span>
              <h3>Chargebacks and Unauthorized Transactions</h3>
            </div>
            <p>
              Users are expected to contact Creators Dost support first before raising a formal bank dispute or payment chargeback for any legitimate transaction.
            </p>
            <p>
              If an account is flagged for fraudulent claims, suspicious dispute abuse, or unauthorized card usage, Creators Dost reserves the right to suspend or terminate account access and pursue investigation.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">10</span>
              <h3>Contact for Payment & Access Concerns</h3>
            </div>
            <p>For payment verification, billing errors, or technical access barriers, contact our support team at:</p>
            <p style={{ marginTop: "8px", fontWeight: "700", color: "#38bdf8" }}>
              Creators Dost • Email: contact@creatorsdost.com
            </p>
            <p style={{ marginTop: "12px", marginBottom: "6px" }}>Please include the following information in your request:</p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Registered Full Name</li>
              <li><span className="dot">✓</span> Registered Email Address or Mobile Number</li>
              <li><span className="dot">✓</span> Enrolled Course Name</li>
              <li><span className="dot">✓</span> Transaction / Order ID</li>
              <li><span className="dot">✓</span> Date and Time of Payment</li>
              <li><span className="dot">✓</span> Detailed description of the access or payment issue</li>
            </ul>
          </article>
        </div>

        <div className="legal-support-strip">
          <div>
            <p>Experiencing billing or technical access issues?</p>
            <strong>Our support desk is available to assist your account.</strong>
          </div>
          <a href="mailto:contact@creatorsdost.com" className="legal-contact-action-btn">
            ✉️ Contact Support Desk
          </a>
        </div>
      </div>
    </div>
  );
}